import { cn } from '@/lib/utils';
import { RAGStatus, designTokens } from '@/lib/theme';

interface DaysOfCoverBadgeProps {
  days: number;
  greenThreshold?: number;
  amberThreshold?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function getDOCStatus(
  days: number,
  greenThreshold: number,
  amberThreshold: number
): RAGStatus {
  if (days === Infinity) return 'green';
  if (days >= greenThreshold) return 'green';
  if (days >= amberThreshold) return 'amber';
  return 'red';
}

const statusBackgrounds: Record<RAGStatus, string> = {
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
};

const sizeStyles = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
};

export function DaysOfCoverBadge({
  days,
  greenThreshold = 14,
  amberThreshold = 7,
  size = 'md',
  className,
}: DaysOfCoverBadgeProps) {
  const status = getDOCStatus(days, greenThreshold, amberThreshold);
  const displayValue = days === Infinity ? '∞' : `${days}d`;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        statusBackgrounds[status],
        sizeStyles[size],
        className
      )}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: designTokens.rag[status] }}
      />
      {displayValue}
    </span>
  );
}

interface StockStatusBadgeProps {
  quantityOnHand: number;
  minLevel: number | null;
  maxLevel: number | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function StockStatusBadge({
  quantityOnHand,
  minLevel,
  maxLevel,
  size = 'md',
  className,
}: StockStatusBadgeProps) {
  let status: RAGStatus;
  let label: string;

  if (quantityOnHand <= 0) {
    status = 'red';
    label = 'Out of Stock';
  } else if (minLevel !== null && quantityOnHand < minLevel) {
    status = 'red';
    label = 'Low';
  } else if (maxLevel !== null && quantityOnHand > maxLevel) {
    status = 'amber';
    label = 'Overstock';
  } else {
    status = 'green';
    label = 'OK';
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium',
        statusBackgrounds[status],
        sizeStyles[size],
        className
      )}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: designTokens.rag[status] }}
      />
      {label}
    </span>
  );
}

export default DaysOfCoverBadge;
